/**
 * At-a-glance signal chips for a fact result.
 *
 * The verdict (Supported / Refuted / Not enough info) is the primary state; these
 * chips surface the secondary signals we already store per fact — a suggested
 * correction, an alternative source, an unreadable source, etc. — so the important
 * context reads without opening each record.
 *
 * Detail cards derive chips straight from the FactResult; the results list derives
 * them from flags emitted by the backend list preview (same chip vocabulary).
 */
import type { CSSProperties } from 'react'
import type { FactResult } from '../types'

// Amber for a verdict that holds only as far as it goes: a supported claim
// that leaves out the source's caveats or adds detail it doesn't state. Shared
// by the result card and the results list so the two read the same.
export const SCOPE_AMBER = { fg: '#b9770e', bg: '#fff8e1', bd: '#f1d29a' }

export type ChipKey =
  | 'correction'
  | 'alt'
  | 'unreadable'
  | 'partial'
  | 'abstract'
  | 'summary'
  | 'landing_page'
  | 'paywall'
  | 'login_required'
  | 'bot_wall'
  | 'supplied_text'
  | 'uploaded'
  | 'stale'
  | 'content_changed'
  | 'primary'
  | 'reviewed'

interface ChipStyle {
  label: string
  icon: string
  bg: string
  fg: string
  bd: string
}

export const CHIP_STYLES: Record<ChipKey, ChipStyle> = {
  correction: {
    label: 'Correction',
    icon: '✎',
    bg: '#fff3df',
    fg: '#a86d0c',
    bd: '#f2d49a',
  },
  // Neutral slate (not green): presence of an alternative-source search, NOT a
  // confirmation that an alternative supported the claim.
  alt: {
    label: 'Alt. source',
    icon: '🔎',
    bg: '#eef1f6',
    fg: '#4c6076',
    bd: '#cad6e2',
  },
  unreadable: {
    label: 'Unreadable',
    icon: '⚠',
    bg: '#fceadd',
    fg: '#b95f22',
    bd: '#f2c39c',
  },
  // Deliberately not a warning colour. A partial source (an abstract, a truncated
  // page) is a real document that may well contain the claim, so this qualifies
  // the verdict beside it rather than contradicting it.
  partial: {
    label: 'Partial source',
    icon: '◐',
    bg: '#f4f1ea',
    fg: '#7a6a4f',
    bd: '#ded5c4',
  },
  // content_kind refinements of "partial" — same calm palette, more specific
  // label. An abstract or summary is a real document that may well contain the
  // claim, so it qualifies the verdict rather than contradicting it.
  abstract: {
    label: 'Abstract only',
    icon: '◔',
    bg: '#f4f1ea',
    fg: '#7a6a4f',
    bd: '#ded5c4',
  },
  summary: {
    label: 'Summary',
    icon: '≣',
    bg: '#f4f1ea',
    fg: '#7a6a4f',
    bd: '#ded5c4',
  },
  // A landing/navigation page, not the document itself — a specific kind of
  // unusable source, so it takes the warning palette like "unreadable".
  landing_page: {
    label: 'Landing page',
    icon: '⚠',
    bg: '#fceadd',
    fg: '#b95f22',
    bd: '#f2c39c',
  },
  // Access barriers — why there was no more of the document. Distinct from the
  // content-quality chip beside them, and actionable: a paywalled article is one
  // the reader may be able to supply themselves, where no fetcher we add could.
  paywall: {
    label: 'Paywalled',
    icon: '🔒',
    bg: '#fdf0f3',
    fg: '#a63d56',
    bd: '#f0c6d1',
  },
  login_required: {
    label: 'Login required',
    icon: '🔑',
    bg: '#fdf0f3',
    fg: '#a63d56',
    bd: '#f0c6d1',
  },
  bot_wall: {
    label: 'Bot-blocked',
    icon: '⛔',
    bg: '#fdf0f3',
    fg: '#a63d56',
    bd: '#f0c6d1',
  },
  // Provenance qualifiers. The verdict was NOT reached against an independently
  // fetched source: the caller supplied the text (self-certifying), or a human
  // uploaded the document. Muted, distinct tones — a caveat, not an error.
  supplied_text: {
    label: 'Agent text',
    icon: '⌨',
    bg: '#f0eef6',
    fg: '#6a5a8f',
    bd: '#d6cee8',
  },
  uploaded: {
    label: 'Uploaded',
    icon: '⬆',
    bg: '#e8eef2',
    fg: '#436074',
    bd: '#c4d4de',
  },
  // Freshness qualifiers (#108). Not the warning palette: stale content is a
  // real document that usually still supports the same verdict — the caveat is
  // about *when* we read it, not whether it was readable. "Changed" is the
  // louder of the two, since the page moved under an earlier verdict.
  stale: {
    label: 'Stale copy',
    icon: '⏳',
    bg: '#f1f0ec',
    fg: '#6f6a5c',
    bd: '#d8d3c6',
  },
  content_changed: {
    label: 'Source changed',
    icon: '⇄',
    bg: '#fdf0e6',
    fg: '#a2662c',
    bd: '#eecdab',
  },
  primary: { label: 'Primary', icon: '★', bg: '#e9f0fb', fg: '#2160c4', bd: '#bdd4f2' },
  reviewed: { label: 'Reviewed', icon: '◆', bg: '#efe9f8', fg: '#6f4bb0', bd: '#d3c4ec' },
}

/**
 * Secondary-signal chips for a fact (verdict is shown separately by the card
 * headline / list badge, so it is not repeated here).
 */
export function secondaryChipKeys(fact: FactResult): ChipKey[] {
  const keys: ChipKey[] = []
  // Mirrors get_preview_badges() in app/database/schemas.py: a supported claim
  // that leaves out caveats has the qualified claim as corrected_claim, not a
  // correction. A refuted broad claim keeps the correction chip.
  // The card's verdict banner already says "missing caveats" or "narrower
  // scope", so no chip here; only skip the correction chip, since a qualified
  // claim is not a correction.
  const scoped =
    fact.classification === 'SUPPORTS' &&
    (fact.scope === 'claim_broader' || fact.scope === 'claim_narrower')
  if (fact.corrected_claim && !scoped) keys.push('correction')
  if (
    (fact.source_search_candidates?.length ?? 0) > 0 ||
    (fact.source_search_history?.length ?? 0) > 0
  ) {
    keys.push('alt')
  }
  // Mirrors quality_chip_keys() in app/validation/content_quality.py: one
  // content-quality chip, most-specific-wins, refined by content_kind. These are
  // exclusive — calling a source that isn't there "partial" says nothing about it.
  if (fact.content_kind === 'landing_page') keys.push('landing_page')
  else if (fact.failure_type || fact.is_garbage_content) keys.push('unreadable')
  else if (fact.content_kind === 'abstract_only') keys.push('abstract')
  else if (fact.content_kind === 'summary') keys.push('summary')
  else if (fact.content_appears_incomplete) keys.push('partial')
  // Access barrier (additive): says why the document was incomplete, where the
  // content-quality chip only says that it was. Mirrors access_chip_keys() in
  // app/validation/content_quality.py.
  if (fact.access_barrier === 'paywall') keys.push('paywall')
  else if (fact.access_barrier === 'login_required') keys.push('login_required')
  else if (fact.access_barrier === 'bot_wall') keys.push('bot_wall')
  // Source provenance (additive, independent of the content-quality chip above):
  // only the trust-qualifying origins show. Mirrors provenance_chip_keys() in
  // app/validation/content_quality.py.
  if (fact.source_provenance === 'supplied_text') keys.push('supplied_text')
  else if (fact.source_provenance === 'user_uploaded') keys.push('uploaded')
  // Cache freshness (additive again — a stale copy can also be an abstract).
  // Mirrors freshness_chip_keys() in app/validation/content_quality.py.
  if (fact.served_stale) keys.push('stale')
  if (fact.content_changed_since) keys.push('content_changed')
  if (fact.is_primary_source) keys.push('primary')
  if (fact.human_validation) keys.push('reviewed')
  return keys
}

export function Chip({ chipKey }: { chipKey: ChipKey }) {
  const s = CHIP_STYLES[chipKey]
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.34em',
        fontSize: '0.72rem',
        fontWeight: 600,
        lineHeight: 1,
        padding: '0.32em 0.62em',
        borderRadius: '999px',
        background: s.bg,
        color: s.fg,
        border: `1px solid ${s.bd}`,
        whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: '0.9em' }}>
        {s.icon}
      </span>
      {s.label}
    </span>
  )
}

export function ChipRow({ chipKeys, style }: { chipKeys: ChipKey[]; style?: CSSProperties }) {
  if (chipKeys.length === 0) return null
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', ...style }}>
      {chipKeys.map((k) => (
        <Chip key={k} chipKey={k} />
      ))}
    </div>
  )
}

/** Filter arbitrary strings (e.g. from the backend list preview) down to known chip keys. */
export function toChipKeys(keys: string[] | undefined): ChipKey[] {
  if (!keys) return []
  return keys.filter((k): k is ChipKey => k in CHIP_STYLES)
}
